import classNames from "classnames";
import { startCase, uniqueId } from "lodash";
import { FC, useContext, useMemo, useRef, useEffect, useState } from "react";
import { ExportGapFilterType } from "@app/exports/enumerations";
import { RegionSetFilter } from "@app/exports/exportModels";
import { NetworkAssessmentAnalysisType, RegionSetStatusType } from "@app/projects/enumerators";
import SpecialtySummarySelectStatusFilter from "./SpecialtySummarySelectStatusFilter";
import { UserContext } from "@app/user/UserContext";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import Roles from "@app/user/roles";

interface SpecialtySummarySelectRegionSetFilterProps {
  disabled: boolean;
  onNetworkAssessmentAnalysisTypeChange: (networkAssessmentAnalysisType: NetworkAssessmentAnalysisType) => void;
  onSecondaryNetworkAssessmentAnalysisTypeChange?: (secondaryNetworkAssessmentAnalysisType: NetworkAssessmentAnalysisType) => void;
  onRegionSetFilterTypeChange: (filter: ExportGapFilterType) => void;
  onRegionSetIncludeChange: (selected: boolean, regionSetStatus: RegionSetStatusType) => void;
  pluralDisplay: string;
  regionSetFilter: RegionSetFilter;
  singularDisplay: string;
  testIdSuffix: string;
}

const SpecialtySummarySelectRegionSetFilter: FC<SpecialtySummarySelectRegionSetFilterProps> = (props: SpecialtySummarySelectRegionSetFilterProps) => {
  const userContext = useContext(UserContext);
  const testIdSuffix = useRef("select-region-set-filter").current;

  const isOpportunityAssessmentAnalysisType = (assessmentAnalysisType: NetworkAssessmentAnalysisType) => {
    return [NetworkAssessmentAnalysisType.Opportunity,
    NetworkAssessmentAnalysisType.ProjectedOpportunity,
    NetworkAssessmentAnalysisType.TermedOpportunity,
    NetworkAssessmentAnalysisType.ProjectedTermedOpportunity]
      .includes(assessmentAnalysisType);
  }

  const [isCurrentServiceAreaAnalysisType, setCurrentServiceAreaAnalysisType] = useState<boolean>(true);

  const regionSetFilters = useMemo(() => {
    const filters = [
      { key: ExportGapFilterType.All, value: `All ${startCase(props.pluralDisplay)}` },
      { key: ExportGapFilterType.Gaps, value: `${startCase(props.pluralDisplay)} with Gaps` },
      { key: ExportGapFilterType.FillableBySingleProvider, value: `${startCase(props.pluralDisplay)} with Gaps Fillable by Single Provider` },
      { key: ExportGapFilterType.Exceptions, value: `${startCase(props.pluralDisplay)} with Exceptions` },
    ];
    if (userContext.roles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER)) {
      filters.push(
        { key: ExportGapFilterType.FillableGaps, value: `${startCase(props.pluralDisplay)} with Fillable Gaps` }
      )
    }
    return filters;
  }, [userContext.roles]);

  const hasIncludes = props.regionSetFilter.includeActive || props.regionSetFilter.includePending || props.regionSetFilter.includeApproved;
  const includeActiveId = useRef(uniqueId("include-active-regionsets")).current;
  const includePendingId = useRef(uniqueId("include-pending-regionsets")).current;
  const includeApprovedId = useRef(uniqueId("include-approved-regionsets")).current;

  useEffect(() => {
    setCurrentServiceAreaAnalysisType(
      props.regionSetFilter.networkAssessmentAnalysisType != null && 
      (!isOpportunityAssessmentAnalysisType(props.regionSetFilter.networkAssessmentAnalysisType) ||
      !isOpportunityAssessmentAnalysisType(props.regionSetFilter.secondaryNetworkAssessmentAnalysisType))
    )
  }, [props.regionSetFilter.networkAssessmentAnalysisType, props.regionSetFilter.secondaryNetworkAssessmentAnalysisType]);

  return (
    <>
      <p className="lead mb0 demibold">{startCase(props.singularDisplay)} Filter{props.disabled && <span>: Selected</span>}</p>
      <p>Please select the {props.pluralDisplay} to include:</p>

      <div className={classNames("form-group row", { "text-danger": !hasIncludes })}>
        <label className="col-12 col-form-label ptb0 text-start">
          Include {!hasIncludes && <i className="fa fa-exclamation-triangle mt5" style={{ position: "absolute" }} />}
        </label>
        <SpecialtySummarySelectStatusFilter
          disabled={false}
          onNetworkAssessmentAnalysisTypeChange={props.onNetworkAssessmentAnalysisTypeChange}
          onSecondaryNetworkAssessmentAnalysisTypeChange={props.onSecondaryNetworkAssessmentAnalysisTypeChange}
          regionSetFilter={props.regionSetFilter}
          testIdSuffix={testIdSuffix}
        />
        <div className="col-12 mt10">
          <QESCheckbox
            className="mt-0"
            label={`Active ${startCase(props.pluralDisplay)}`}
            id={includeActiveId}
            disabled={props.disabled || !isCurrentServiceAreaAnalysisType}
            value="active"
            checked={props.regionSetFilter.includeActive && isCurrentServiceAreaAnalysisType}
            onChange={(e) => props.onRegionSetIncludeChange(e.target.checked, RegionSetStatusType.Active)}
            testId="select-region-set-filter__active"
          />
          <QESCheckbox
            className="mt-0"
            label={`Pending ${startCase(props.pluralDisplay)}`}
            id={includePendingId}
            disabled={props.disabled || !isCurrentServiceAreaAnalysisType}
            value="pending"
            checked={props.regionSetFilter.includePending && isCurrentServiceAreaAnalysisType}
            onChange={(e) => props.onRegionSetIncludeChange(e.target.checked, RegionSetStatusType.Pending)}
            testId="select-region-set-filter__pending"
          />
          <QESCheckbox
            className="mt-0"
            label={`Approved ${startCase(props.pluralDisplay)}`}
            id={includeApprovedId}
            disabled={props.disabled || !isCurrentServiceAreaAnalysisType}
            value="approved"
            checked={props.regionSetFilter.includeApproved && isCurrentServiceAreaAnalysisType}
            onChange={(e) => props.onRegionSetIncludeChange(e.target.checked, RegionSetStatusType.Approved)}
            testId="select-region-set-filter__approved"
          />
        </div>
      </div>
      <div className="form-group row qes-radio-group__container">
        <p className="col-12 col-form-label ptb0 text-start mb-1">Filter</p>
        {regionSetFilters.map(({ key, value }) => {
          return (
            <div key={key} className="col-12 mt0">
              <QESRadioButton
                label={value}
                className="mt-0"
                disabled={props.disabled}
                value={key}
                checked={props.regionSetFilter.filterType === key}
                onChange={() => props.onRegionSetFilterTypeChange(key)}
                testId={`select-region-filter__${key}--${props.testIdSuffix}`}
              />
            </div>
          );
        })}
      </div>
    </>
  );
}

export default SpecialtySummarySelectRegionSetFilter;
